/* STRE — home, menu, flag language menu and motion (LUMEA, autumn / luxury edition). Owner: home page team.
   Loaded after theme.js (defer) and never replaces its handlers: theme.js still opens the menu / bag, adds to the bag
   and re-renders sections; this file only listens and adds motion around it.
   1 language menu · 2 tabs (menu + shelves) · 3 search button · 4 hero slideshow · 5 parallax · 6 '+' flies to the bag
   7 numbers glide to their new value · 8 photos open softly · 9 accordion height · 10 awards strip
   11 theme editor: a section re-drawn in place is set up again (shopify:section:load).
   Motion uses transform / opacity only and stops when "reduce motion" is on. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const root = document.documentElement;
  const isRTL = root.dir === 'rtl';
  const reduceQuery = matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = () => reduceQuery.matches;
  const safely = (fn) => { try { fn(); } catch (e) { console.warn(e); } };

  /* ---------- 1. language menu: flag button → small list (Esc, tap outside, arrow keys) ---------- */
  let openLang = null;
  const langOptions = (menu) => $$('.lmenu__opt', menu);

  function openLangMenu(menu, focusItem) {
    if (openLang && openLang !== menu) closeLangMenu(openLang);
    const pop = $('[data-lmenu-pop]', menu);
    const btn = $('[data-lmenu-toggle]', menu);
    if (!pop || !btn) return;
    clearTimeout(menu.lmenuTimer);
    pop.hidden = false;
    void pop.offsetWidth; // commit the closed state so the list grows in
    menu.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    openLang = menu;
    if (focusItem) {
      const current = $('.lmenu__opt[aria-current="true"]', menu) || langOptions(menu)[0];
      if (current) current.focus({ preventScroll: true });
    }
  }

  function closeLangMenu(menu, refocus = false) {
    if (!menu) return;
    const pop = $('[data-lmenu-pop]', menu);
    const btn = $('[data-lmenu-toggle]', menu);
    menu.classList.remove('is-open');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    clearTimeout(menu.lmenuTimer);
    menu.lmenuTimer = setTimeout(() => {
      if (pop && !menu.classList.contains('is-open')) pop.hidden = true;
    }, reduce() ? 0 : 260);
    if (openLang === menu) openLang = null;
    if (refocus && btn) btn.focus({ preventScroll: true });
  }

  /* ---------- 2. tabs: side menu categories + home shelves ---------- */
  function selectTab(tab, focus = false) {
    const list = tab.closest('[role="tablist"]');
    if (!list) return;
    $$('[role="tab"]', list).forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus({ preventScroll: true });
    // keep the chosen tab in view inside its sideways row
    const r = list.getBoundingClientRect();
    const c = tab.getBoundingClientRect();
    if (c.left < r.left + 8 || c.right > r.right - 8) {
      list.scrollBy({ left: (c.left + c.width / 2) - (r.left + r.width / 2), behavior: reduce() ? 'auto' : 'smooth' });
    }
  }

  function tabKeys(e) {
    const tab = e.target.closest && e.target.closest('[data-tabs] [role="tab"]');
    if (!tab) return false;
    const tabs = $$('[role="tab"]', tab.closest('[role="tablist"]'));
    const i = tabs.indexOf(tab);
    let next = -1;
    const fwd = isRTL ? 'ArrowLeft' : 'ArrowRight';
    const back = isRTL ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === fwd) next = (i + 1) % tabs.length;
    else if (e.key === back) next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return false;
    e.preventDefault();
    selectTab(tabs[next], true);
    return true;
  }

  /* ---------- clicks (theme.js registered first, so the menu is already open here) ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    const toggle = t.closest('[data-lmenu-toggle]');
    if (toggle) {
      const menu = toggle.closest('[data-lmenu]');
      if (menu.classList.contains('is-open')) closeLangMenu(menu);
      else openLangMenu(menu, e.detail === 0); // keyboard (Enter / Space) moves into the list
      return;
    }
    const option = t.closest('.lmenu__opt');
    if (option) {
      // the current language: just close the list (any other option submits the Shopify localization form)
      if (option.getAttribute('aria-current') === 'true') {
        e.preventDefault();
        closeLangMenu(option.closest('[data-lmenu]'), true);
      }
      return;
    }
    if (openLang && !t.closest('[data-lmenu]')) closeLangMenu(openLang);

    // header search: theme.js has just opened the side menu, put the cursor in its search field (keyboard on iPhone)
    if (t.closest('[data-search-open]')) {
      e.preventDefault();
      const input = $('[data-menu-search]');
      if (input) input.focus({ preventScroll: true });
      return;
    }

    const tab = t.closest('[data-tabs] [role="tab"]');
    if (tab) selectTab(tab);
  });

  // capture phase: Esc closes the language list first (theme.js would otherwise close the whole menu)
  document.addEventListener('keydown', (e) => {
    if (tabKeys(e)) return;
    const toggle = e.target.closest && e.target.closest('[data-lmenu-toggle]');
    if (toggle && !openLang && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      openLangMenu(toggle.closest('[data-lmenu]'), true);
      return;
    }
    if (!openLang) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeLangMenu(openLang, true);
      return;
    }
    const items = langOptions(openLang);
    const i = items.indexOf(document.activeElement);
    let next = -1;
    if (e.key === 'ArrowDown') next = i < 0 ? 0 : (i + 1) % items.length;
    else if (e.key === 'ArrowUp') next = i < 0 ? items.length - 1 : (i - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next >= 0) {
      e.preventDefault();
      items[next].focus();
    } else if (e.key === 'Tab') {
      const menu = openLang;
      setTimeout(() => { if (openLang === menu && !menu.contains(document.activeElement)) closeLangMenu(menu); }, 0);
    }
  }, true);

  window.addEventListener('pageshow', () => { if (openLang) closeLangMenu(openLang); });

  /* ---------- 4. hero: slow cross-fade between campaign photos, progress bars, pause, swipe ----------
     The bar under the photo fills with a CSS animation (lumea-home.css, hsFill); when it ends the next photo shows.
     Pausing = a class on the hero (held / paused / off screen); the open menu or bag pauses it from CSS. */
  const heroStops = new WeakMap();

  // photos after the first are only named in data-src / data-srcset (snippets/home-image): load them after the page
  function hydrate(img) {
    if (!img || !img.dataset.srcset) return;
    img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }

  function stopHero(hero) {
    const stop = heroStops.get(hero);
    if (stop) { stop(); heroStops.delete(hero); }
  }

  function initHero(hero) {
    stopHero(hero);
    const slides = $$('[data-slide]', hero);
    if (slides.length < 2) return;
    if (reduce() || !('IntersectionObserver' in window)) {
      hero.classList.add('is-still'); // only the first photo is shown, so the others are never downloaded
      return;
    }
    const dots = $$('.hs__dots i', hero);
    const ms = Number(hero.dataset.interval) || 6000;
    let timer = 0;
    const ac = new AbortController();
    const opt = { signal: ac.signal };
    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    let visible = true;

    const loadRest = () => {
      const run = () => slides.forEach((s) => $$('img[data-srcset]', s).forEach(hydrate));
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
      else setTimeout(run, 1200);
    };
    if (document.readyState === 'complete') loadRest();
    else window.addEventListener('load', loadRest, { once: true, signal: ac.signal });

    function show(n) {
      const prev = slides[index];
      index = (n + slides.length) % slides.length;
      const next = slides[index];
      if (prev === next) return;
      $$('img[data-srcset]', next).forEach(hydrate);
      prev.classList.remove('is-active');
      prev.classList.add('was-active');
      prev.setAttribute('aria-hidden', 'true');
      prev.setAttribute('inert', '');
      next.classList.remove('was-active');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      next.removeAttribute('inert');
      setTimeout(() => { if (!prev.classList.contains('is-active')) prev.classList.remove('was-active'); }, 2000); // after the 1.8 s cross-fade
      dots.forEach((dot, i) => dot.classList.toggle('is-on', i === index));
    }

    // a plain timer turns the photos (Apple edition: no progress bars, no pause button); it waits while the photo is
    // off screen, the tab is hidden, a menu or the bag is open, or a pointer / keyboard focus rests on it
    const held = () => hero.classList.contains('is-off') || hero.classList.contains('is-held')
      || document.documentElement.classList.contains('menu-open') || document.documentElement.classList.contains('cart-open');
    const tick = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (!held()) show(index + 1); tick(); }, ms);
    };
    tick();

    const setOff = () => hero.classList.toggle('is-off', !visible || document.hidden);
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; setOff(); });
    io.observe(hero);
    document.addEventListener('visibilitychange', setOff, opt);
    // keyboard focus or a mouse resting on the photo holds the slide
    hero.addEventListener('focusin', (e) => {
      let keyboard = true;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = true; }
      if (keyboard) hero.classList.add('is-held');
    }, opt);
    hero.addEventListener('focusout', () => hero.classList.remove('is-held'), opt);
    hero.addEventListener('mouseenter', () => hero.classList.add('is-held'), opt);
    hero.addEventListener('mouseleave', () => hero.classList.remove('is-held'), opt);
    // swipe sideways to the next / previous photo
    let sx = null;
    let sy = 0;
    hero.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true, signal: ac.signal });
    hero.addEventListener('touchend', (e) => {
      if (sx == null) return;
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      sx = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) { show(index + ((dx < 0) !== isRTL ? 1 : -1)); tick(); }
    }, { passive: true, signal: ac.signal });

    heroStops.set(hero, () => { ac.abort(); io.disconnect(); clearTimeout(timer); });
  }

  /* ---------- 5. parallax: photos drift a little slower than the page ---------- */
  const paraActive = new Set();
  let paraIO = null;
  let paraTicking = false;
  function paraUpdate() {
    paraTicking = false;
    const vh = window.innerHeight || 1;
    paraActive.forEach((el) => {
      if (!el.isConnected) { paraActive.delete(el); return; }
      const frame = el.parentElement.getBoundingClientRect();
      const max = Number(el.dataset.parallax) || 30;
      const p = Math.max(-1, Math.min(1, ((frame.top + frame.height / 2) - vh / 2) / (vh / 2 + frame.height / 2)));
      el.style.translate = `0 ${(-p * max).toFixed(1)}px`;
    });
  }
  const paraSchedule = () => { if (!paraTicking) { paraTicking = true; requestAnimationFrame(paraUpdate); } };
  function initParallax(scope = document) {
    if (reduce() || !('IntersectionObserver' in window)) return;
    const els = $$('[data-parallax]', scope);
    if (!els.length) return;
    if (!paraIO) {
      paraIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) paraActive.add(en.target); else paraActive.delete(en.target); });
        paraSchedule();
      }, { rootMargin: '15% 0px' });
      window.addEventListener('scroll', paraSchedule, { passive: true });
      window.addEventListener('resize', paraSchedule);
    }
    els.forEach((el) => paraIO.observe(el));
  }

  /* ---------- 6. '+' → the product photo flies into the Bag pill, the count bumps when it lands ---------- */
  function bumpBag() {
    const cnt = $('#cart-icon-bubble .cnt');
    if (cnt) { cnt.classList.remove('bump'); void cnt.offsetWidth; cnt.classList.add('bump'); }
    const bag = $('.hdr .bag');
    if (bag) { bag.classList.remove('is-land'); void bag.offsetWidth; bag.classList.add('is-land'); }
  }

  function flyToBag(trigger, pick = null) {
    const bag = $('.hdr .bag');
    if (!bag || reduce() || !('animate' in Element.prototype)) { bumpBag(); return; }
    // nearest photo: the row's own packshot, else the card / step it sits in (compact rows have no photo)
    let img = pick;
    for (let host = img ? null : trigger.closest('.card, .prow, .rstep, .lstep, [data-fly-root]'); host && !img; host = host.parentElement && host.parentElement.closest('.card, .rstep, .lstep, [data-fly-root]')) {
      img = $('.card__img img, .prow__img img, .lstep__sw img, .rstep__img img', host);
    }
    const photo = img && (img.currentSrc || img.src) ? img : null;
    // a photo scrolled out of sight (sticky bar at the bottom of the page) starts from the button as a round thumbnail
    const pr = photo && photo.getBoundingClientRect();
    const inView = pr && pr.width && pr.bottom > 60 && pr.top < innerHeight - 60;
    const from = (inView ? photo : trigger).getBoundingClientRect();
    const to = bag.getBoundingClientRect();
    if (!from.width || !to.width) { bumpBag(); return; }
    const size = Math.round(Math.max(28, Math.min(from.width, from.height, 110)));
    const sx = from.left + from.width / 2 - size / 2;
    const sy = from.top + from.height / 2 - size / 2;
    const ex = to.left + to.width / 2 - size / 2;
    const ey = to.top + to.height / 2 - size / 2;
    const end = 16 / size;

    const fly = document.createElement('div');
    fly.className = photo ? (inView ? 'fly' : 'fly fly--round') : 'fly fly--dot';
    fly.setAttribute('aria-hidden', 'true');
    fly.style.width = `${size}px`;
    fly.style.height = `${size}px`;
    const inner = document.createElement('span');
    inner.className = 'fly__in';
    if (photo) inner.style.backgroundImage = `url("${(photo.currentSrc || photo.src).replace(/"/g, '%22')}")`;
    fly.appendChild(inner);
    document.body.appendChild(fly);

    // x glides evenly, y rises first and then drops: together they draw an arc; the photo lifts, turns a little and
    // shrinks into the bag (0.95 s, Arda: "çok düz ve hızlı")
    const timing = { duration: 950, fill: 'forwards' };
    fly.animate([{ transform: `translateX(${sx}px)` }, { transform: `translateX(${ex}px)` }], { ...timing, easing: 'cubic-bezier(.45, 0, .55, 1)' });
    const drop = inner.animate([
      { transform: `translateY(${sy}px) scale(1) rotate(0deg)`, opacity: 1, borderRadius: inView ? '14px' : '50%' },
      { transform: `translateY(${ey}px) scale(${end}) rotate(${18 * (document.dir === 'rtl' ? -1 : 1)}deg)`, opacity: 0.9, borderRadius: '50%' }
    ], { ...timing, easing: 'cubic-bezier(.5, -0.55, .7, 1)' });
    const done = () => { fly.remove(); bumpBag(); };
    drop.onfinish = done;
    drop.oncancel = done;
  }

  // product page / sticky bar / quick add: theme.js says stre:added once the add is confirmed; the photo on screen (the
  // gallery slide in view) flies, the bag count bumps when it lands
  document.addEventListener('stre:added', (e) => {
    const trigger = e.detail && e.detail.trigger;
    if (!trigger || !trigger.isConnected) return;
    const host = trigger.closest('[data-product], [data-qa-body]');
    const imgs = host ? [...host.querySelectorAll('[data-gallery] > li:not([hidden]) img, .qa__img img, .qa__media img')] : [];
    const seen = imgs.find((i) => { const r = i.getBoundingClientRect(); return r.width && r.left >= -2 && r.right <= innerWidth + 2 && r.bottom > 60 && r.top < innerHeight - 60; });
    const cnt = $('#cart-icon-bubble .cnt');
    if (cnt) cnt.classList.remove('bump');
    flyToBag(trigger, seen || imgs[0] || null);
  });

  // fly only after theme.js confirms the add (the '+' turns into ✓); a failed add never flies
  document.addEventListener('click', (e) => {
    const add = e.target instanceof Element && e.target.closest('.add[data-quick-add]');
    if (!add) return;
    const watch = new MutationObserver(() => {
      if (!add.classList.contains('is-done')) return;
      watch.disconnect();
      clearTimeout(stop);
      const cnt = $('#cart-icon-bubble .cnt');
      if (cnt) cnt.classList.remove('bump'); // the count bumps when the photo lands instead
      flyToBag(add);
    });
    watch.observe(add, { attributes: true, attributeFilter: ['class'] });
    const stop = setTimeout(() => watch.disconnect(), 8000);
  });

  /* ---------- 7. prices and totals glide to their new value ---------- */
  function parseAmount(text) {
    const m = String(text).match(/(\d[\d,]*)(\.\d+)?/);
    if (!m) return null;
    return { value: parseFloat(m[1].replace(/,/g, '') + (m[2] || '')), decimals: m[2] ? m[2].length - 1 : 0, start: m.index, len: m[0].length };
  }

  function glide(el, fromText, toText) {
    if (reduce()) return;
    const a = parseAmount(fromText);
    const b = parseAmount(toText);
    if (!a || !b || a.value === b.value) return;
    const pre = toText.slice(0, b.start);
    const post = toText.slice(b.start + b.len);
    const format = (v) => {
      const [int, frac] = v.toFixed(b.decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return pre + (frac ? `${grouped}.${frac}` : grouped) + post;
    };
    if (format(b.value) !== toText) return; // unknown number format: leave it alone
    if (el.glideFrame) cancelAnimationFrame(el.glideFrame);
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / 520);
      const eased = 1 - Math.pow(1 - k, 3);
      const text = k < 1 ? format(a.value + (b.value - a.value) * eased) : toText;
      el.glideText = text;
      el.textContent = text;
      el.glideFrame = k < 1 ? requestAnimationFrame(step) : 0;
    };
    el.glideText = format(a.value);
    el.textContent = el.glideText;
    el.glideFrame = requestAnimationFrame(step);
  }

  function initNumbers() {
    if (!('MutationObserver' in window)) return;
    const INNER = '[data-cart-drawer-inner], [data-cart-section-inner]';
    const NUMS = '.cart__foot .trow .num, .ssum .num';
    new MutationObserver((records) => {
      records.forEach((m) => {
        // product page price: theme.js swaps the text of [data-price-display]
        if (m.target instanceof Element && m.target.matches('[data-price-display]')) {
          const el = m.target;
          if (el.textContent === el.glideText) return;
          const old = [...m.removedNodes].map((n) => n.textContent).join('');
          if (old) glide(el, old, el.textContent);
          return;
        }
        // bag sheet and home sticky bar: the whole inner block is replaced after each change
        m.removedNodes.forEach((gone) => {
          if (!(gone instanceof Element) || !gone.matches(INNER)) return;
          const fresh = [...m.addedNodes].find((n) => n instanceof Element && n.matches(INNER));
          if (!fresh) return;
          const before = $$(NUMS, gone);
          $$(NUMS, fresh).forEach((el, i) => {
            const old = before[i];
            if (old && old.textContent !== el.textContent) glide(el, old.textContent, el.textContent);
          });
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- 8. photos open softly once loaded (never hides a photo that is already there) ---------- */
  function initPhotoFade(scope = document) {
    if (reduce()) return;
    $$('.card__img img, .prow__img img, .ctile__img img, .rstep__img img, .lstep__sw img, .mfeat__img img, .camp__media img, .rfeat__media img, .edit__media img, .shade__pan img', scope).forEach((img) => {
      if (img.complete && img.naturalWidth) return;
      img.classList.add('img-fade', 'img-wait');
      const show = () => img.classList.remove('img-wait');
      img.addEventListener('load', show, { once: true });
      img.addEventListener('error', show, { once: true });
      setTimeout(show, 8000); // never leave a photo hidden
    });
  }

  /* ---------- 9. accordions (details.acc): the height glides open and shut ---------- */
  document.addEventListener('click', (e) => {
    const summary = e.target instanceof Element && e.target.closest('details.acc > summary');
    if (!summary || e.defaultPrevented || reduce() || !('animate' in Element.prototype)) return;
    const details = summary.parentElement;
    if (details.hasAttribute('data-no-motion')) return;
    e.preventDefault();
    if (details.accAnim) details.accAnim.cancel();
    const start = details.offsetHeight;
    const borders = details.offsetHeight - details.clientHeight;
    let end;
    details.style.overflow = 'hidden';
    if (!details.open || details.classList.contains('is-closing')) {
      details.classList.remove('is-closing');
      details.open = true;
      end = details.offsetHeight;
    } else {
      details.classList.add('is-closing');
      end = summary.offsetHeight + borders;
    }
    // a long panel opens / closes at once (its text still fades in): no page re-layout on every frame
    if (Math.abs(end - start) > 300) {
      if (details.classList.contains('is-closing')) { details.open = false; details.classList.remove('is-closing'); }
      details.style.overflow = '';
      return;
    }
    const anim = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: 340, easing: 'cubic-bezier(.22, .8, .3, 1)' });
    details.accAnim = anim;
    anim.onfinish = () => {
      details.accAnim = null;
      if (details.classList.contains('is-closing')) {
        details.open = false;
        details.classList.remove('is-closing');
      }
      details.style.overflow = '';
    };
    anim.oncancel = () => { details.style.overflow = ''; };
  });

  /* ---------- 10. awards strip: a pause button (WCAG 2.2.2); it also rests while touched or off screen ---------- */
  function initMarquee(strip) {
    if (strip.dataset.marqReady) return;
    strip.dataset.marqReady = '1';
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => strip.classList.toggle('is-off', !entry.isIntersecting)).observe(strip);
    }
    strip.addEventListener('pointerdown', () => strip.classList.add('is-held'));
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => strip.addEventListener(type, () => strip.classList.remove('is-held')));
    const host = strip.closest('section') || strip.parentElement;
    const btn = host && $('[data-marquee-pause]', host);
    if (btn) {
      btn.addEventListener('click', () => {
        const paused = !strip.classList.contains('is-paused');
        strip.classList.toggle('is-paused', paused);
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      });
    }
  }

  /* ---------- 11. theme editor: Shopify re-draws one section in place after a setting changes ---------- */
  function setUp(scope) {
    $$('[data-hero-slides]', scope).forEach((hero) => safely(() => initHero(hero)));
    safely(() => initParallax(scope));
    safely(() => initPhotoFade(scope));
    $$('[data-marquee]', scope).forEach((strip) => safely(() => initMarquee(strip)));
  }
  document.addEventListener('shopify:section:unload', (e) => {
    $$('[data-hero-slides]', e.target).forEach(stopHero);
  });
  document.addEventListener('shopify:section:load', (e) => setUp(e.target));

  /* ---------- start ---------- */
  setUp(document);
  safely(initNumbers);
})();
